import React, { useEffect, useState } from 'react';
import useAuthOktaUser from '../../../../app/hooks/useAuthOktaUser';
import { Grid, InputAdornment, TextField, Box, Button, Typography, Alert } from '@mui/material';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import styleVariables from '../../../../app/styles/styleVariables';
import { useTranslation } from 'react-i18next';
import langAuthPasswordReset from './langs/langAuthPasswordReset';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AppLoaderContent from '../../../../app/features/loader/content/AppLoaderContent';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import AuthRoutes from '../../AuthRoutes';
import { useNavigate } from 'react-router-dom';

interface IAuthPasswordResetForm {
	newPassword: string;
	newPasswordConfirmation: string;
}

type FAuthPasswordResetProps = {
	oktaRecoveryToken: string;
};
const FAuthPasswordReset = (props: FAuthPasswordResetProps) => {
	const { oktaRecoveryToken } = props;
	const authOktaUser = useAuthOktaUser();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const sxTextField = {
		'& .MuiInputBase-root': {
			border: '1px solid #CCC',
		},
	};

	const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
	useEffect(() => {
		if (oktaRecoveryToken) {
			authOktaUser
				.verifyRecoveryToken({
					recoveryToken: oktaRecoveryToken,
				})
				.then(() => {
					setIsTokenValid(true);
				})
				.catch(() => {
					setIsTokenValid(false);
				});
		}
	}, [authOktaUser, oktaRecoveryToken]);

	const schema = yup
		.object({
			newPassword: yup
				.string()
				.required(t(langAuthPasswordReset.field_error_password_required.tag))
				.min(8, t(langAuthPasswordReset.field_error_password_too_short.tag))
				.matches(/(.*[a-z].*)/, t(langAuthPasswordReset.field_error_password_must_contain_one_lowercase.tag))
				.matches(/(.*[A-Z].*)/, t(langAuthPasswordReset.field_error_password_must_contain_one_uppercase.tag))
				.matches(/(.*\d.*)/, t(langAuthPasswordReset.field_error_password_must_contain_one_number.tag)),
			newPasswordConfirmation: yup
				.mixed()
				.test('match', t(langAuthPasswordReset.field_error_passwords_do_not_match.tag), function (password) {
					return password === this.parent.newPassword;
				})
				.required(t(langAuthPasswordReset.field_error_password_confirm_required.tag)),
		})
		.required();

	const {
		handleSubmit,
		control,
		setError,
		formState: { errors },
	} = useForm<IAuthPasswordResetForm>({ resolver: yupResolver(schema) });

	const [isUpdatePasswordSuccess, setIsUpdatePasswordSuccess] = useState<boolean | null>(null);
	const onSubmit: SubmitHandler<IAuthPasswordResetForm> = (data) => {
		authOktaUser
			.verifyRecoveryToken({
				recoveryToken: oktaRecoveryToken,
			})
			.then(function (transaction: { resetPassword?: (args: { newPassword: string }) => Promise<unknown> }) {
				transaction
					.resetPassword?.({
						newPassword: data.newPassword,
					})
					.then(() => {
						setIsUpdatePasswordSuccess(true);
					})
					.catch((e: unknown) => {
						const summary = (e as { errorCauses?: Array<{ errorSummary?: string }> })?.errorCauses?.[0]
							?.errorSummary;
						setError('newPassword', { type: 'custom', message: summary ?? '' });
						setIsUpdatePasswordSuccess(false);
					});
			})
			.catch(() => {
				setIsTokenValid(false);
			});
	};

	return (
		<>
			{isUpdatePasswordSuccess ? (
				<>
					<Alert severity="success" elevation={0} sx={{ boxShadow: 'none', marginBottom: '30px' }}>
						<Typography variant="body2" gutterBottom sx={{ textAlign: 'left' }}>
							{t(langAuthPasswordReset.text_success.tag)}
						</Typography>
					</Alert>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="button"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							onClick={() => navigate(HAppRoutes.getPath(AuthRoutes.login.tag))}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthPasswordReset.btn_login.tag)}
						</Button>
					</Box>
				</>
			) : (
				<>
					{isTokenValid ? (
						<form onSubmit={handleSubmit(onSubmit)}>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<Controller
										name="newPassword"
										control={control}
										defaultValue=""
										render={({ field }) => (
											<TextField
												fullWidth
												sx={sxTextField}
												label={t(langAuthPasswordReset.label_password.tag)}
												variant="outlined"
												type="password"
												size="medium"
												autoComplete="off"
												{...field}
												error={!!errors.newPassword}
												helperText={
													errors?.newPassword?.message ??
													t(langAuthPasswordReset.label_password_helpertext.tag)
												}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<AppCustomIcon
																tag="auth_password"
																sx={{ color: styleVariables.primaryColor }}
															/>
														</InputAdornment>
													),
												}}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Controller
										name="newPasswordConfirmation"
										control={control}
										defaultValue=""
										render={({ field }) => (
											<TextField
												fullWidth
												sx={sxTextField}
												label={t(langAuthPasswordReset.label_password_confirmation.tag)}
												variant="outlined"
												type="password"
												size="medium"
												autoComplete="off"
												{...field}
												error={!!errors.newPasswordConfirmation}
												helperText={errors?.newPasswordConfirmation?.message}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<AppCustomIcon
																tag="auth_password"
																sx={{ color: styleVariables.primaryColor }}
															/>
														</InputAdornment>
													),
												}}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Box style={{ textAlign: 'center' }}>
										<Button
											type="submit"
											variant="contained"
											color="secondary"
											sx={{ minWidth: 160, fontWeight: 600 }}
											endIcon={<AppCustomIcon tag="arrow-right" />}
										>
											{t(langAuthPasswordReset.btn_reset_password.tag)}
										</Button>
									</Box>
								</Grid>
							</Grid>
						</form>
					) : (
						<>
							{isTokenValid === false ? (
								<>
									<Alert severity="error" elevation={0} sx={{ boxShadow: 'none' }}>
										<Typography variant="body2" gutterBottom sx={{ textAlign: 'left' }}>
											{t(langAuthPasswordReset.text_link_invalid.tag)}
										</Typography>
									</Alert>
								</>
							) : (
								<AppLoaderContent />
							)}
						</>
					)}
				</>
			)}
		</>
	);
};

export default FAuthPasswordReset;
